import { FC, ReactNode } from 'react'
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, SxProps } from '@mui/material'
import { useTheme } from '@mui/material/styles'

interface AlertDialogProps {
  /**
   * Controls whether the dialog is visible
   */
  show: boolean
  /**
   * Callback function when dialog is closed (via OK button, ESC key, or backdrop click)
   */
  onClose: () => void
  /**
   * Title text to display at the top of the dialog
   */
  title?: string
  /**
   * Message content - can be a string or custom React components
   */
  message?: ReactNode
  /**
   * Children components to render inside the dialog content area
   */
  children?: ReactNode
  /**
   * Text for the OK button (default: 'OK')
   */
  okText?: string
  /**
   * Color variant for the OK button (default: 'primary')
   */
  okColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  /**
   * Variant for the OK button (default: 'contained')
   */
  okVariant?: 'text' | 'outlined' | 'contained'
  /**
   * Maximum width of the dialog (default: 'sm')
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  /**
   * Additional styles to apply to the dialog content
   */
  sx?: SxProps
  /**
   * Additional styles to apply to the dialog paper
   */
  paperSx?: SxProps
  /**
   * Content alignment (default: 'center')
   */
  align?: 'left' | 'center' | 'right'
  /**
   * Allow closing via backdrop click (default: true)
   */
  allowBackdropClose?: boolean
  /**
   * Custom action buttons (if provided, replaces the default OK button)
   */
  customActions?: ReactNode
}

const AlertDialog: FC<AlertDialogProps> = ({
  show,
  onClose,
  title,
  message,
  children,
  okText = 'OK',
  okColor = 'primary',
  okVariant = 'contained',
  maxWidth = 'sm',
  sx,
  paperSx,
  align = 'center',
  allowBackdropClose = true,
  customActions,
}) => {
  const theme = useTheme()

  const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' && !allowBackdropClose) {
      return
    }
    onClose()
  }

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          padding: 2,
          ...paperSx,
        },
      }}
    >
      {title && (
        <DialogTitle
          sx={{
            textAlign: align,
            fontSize: '1.5rem',
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
          justifyContent: 'center',
          gap: 2,
          py: 3,
          ...sx,
        }}
      >
        {message && (
          <Typography
            variant="body1"
            sx={{
              textAlign: align,
              color: theme.palette.text.secondary,
            }}
          >
            {message}
          </Typography>
        )}
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
        }}
      >
        {customActions || (
          <Button variant={okVariant} color={okColor} onClick={onClose}>
            {okText}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default AlertDialog
